import HCLTreeMxGraphDecoder from './HCLTreeMxGraphDecoder';
import FaultTreeMxGraph, {FaultTreeMxGraphJSON} from '../HCLTreeMxGraph/FaultTreeMxGraph';
import FaultTreeTransferGateVertex
  from '../HCLTreeNode/FaultTreeNode/FaultTreeTransferGateNode/FaultTreeTransferGateVertex';
import BasicEventVertex from '../HCLTreeNode/FaultTreeNode/BasicEventNode/BasicEventVertex';
import HouseEventVertex from '../HCLTreeNode/FaultTreeNode/HouseEventNode/HouseEventVertex';
import GateVertexBuilder from '../HCLTreeNode/FaultTreeNode/GateNode/GateVertexBuilder';
import {GateVertexValueJSON} from '../HCLTreeNode/FaultTreeNode/GateNode/GateVertexValue';
import BasicEventVertexValue, {BasicEventVertexValueJSON} from '../HCLTreeNode/FaultTreeNode/BasicEventNode/BasicEventVertexValue';
import HouseEventVertexValue, {HouseEventVertexValueJSON} from '../HCLTreeNode/FaultTreeNode/HouseEventNode/HouseEventVertexValue';
import Label from '../HCLTreeNode/HCLTreeVertexValue/Label';
import Formula, {FormulaJSON} from '../HCLTreeNode/HCLTreeVertexValue/Formula';
import Expression from '../HCLTreeNode/HCLTreeVertexValue/Expression';
import Constant from '../HCLTreeNode/HCLTreeVertexValue/Constant';
import ReferenceTypes from '../HCLTreeNode/HCLTreeVertexValue/ReferenceTypes';
import GateVertex from '../HCLTreeNode/FaultTreeNode/GateNode/GateVertex';
import {OutcomeJSON} from '../HCLTreeNode/HCLTreeVertexValue/Outcome';
import {HCLTreeVertexValueJSON} from '../HCLTreeNode/HCLTreeVertexValue/HCLTreeVertexValue';
import InvalidJSONFormatError from './InvalidJSONFormatError';
import FaultTreeTransferGateVertexValue, {FaultTreeTransferGateVertexValueJSON} from '../HCLTreeNode/FaultTreeNode/FaultTreeTransferGateNode/FaultTreeTransferGateVertexValue';
import FaultTreeInstanceTransferGateVertex
  from '../HCLTreeNode/FaultTreeNode/FaultTreeTransferGateNode/FaultTreeInstanceTransferGateNode/FaultTreeInstanceTransferGateVertex';
import FaultTreeInstanceTransferGateVertexValue
  from '../HCLTreeNode/FaultTreeNode/FaultTreeTransferGateNode/FaultTreeInstanceTransferGateNode/FaultTreeInstanceTransferGateVertexValue';
import ProxyTypes from "../HCLTreeNode/HCLTreeVertexValue/ProxyTypes";
import GateTypes from "../HCLTreeNode/HCLTreeVertexValue/GateTypes";
import Utility from "../../Utility";

const _ = require("lodash");

/**
 * Inherits from {@link HCLTreeMxGraphDecoder} and implements codecs for {@link FaultTreeMxGraph}
 */
class FaultTreeMxGraphDecoder extends HCLTreeMxGraphDecoder {

  private gates: {[index: string]: GateVertex } = {};

  private visitedLogicGates: {[index: string]: boolean} = {};
  private commonCause: any;
  private commonCauseFactors: any;

  /**
   * @param {FaultTreeMxGraph} graph
   * @param {FaultTreeMxGraphJSON} json
   */
  constructor(graph: FaultTreeMxGraph, json: FaultTreeMxGraphJSON, commonCause?: any, commonCauseFactors?: any) {
    super(graph, json);
    this.commonCause = commonCause;
    this.commonCauseFactors = commonCauseFactors;
  }

  /**
   * @param {string} key
   * @param {BasicEventVertexValue} json
   * @return {BasicEventVertex}
   */
  protected addBasicEventFromObject(key: string, json: BasicEventVertexValueJSON): BasicEventVertex {
    let value = new BasicEventVertexValue(new Label(key), new Expression(json.expression));
    if (json.label && Object.keys(json.label).length) {
      value = new BasicEventVertexValue(Label.build(json.label), new Expression(json.expression));
    }
    return new BasicEventVertex(key, value);
  }

  // Common Cause Component Group
  protected addCCCGFromObject(key: string): any {
    const treeJSON: FaultTreeMxGraphJSON = this.getJSON() as FaultTreeMxGraphJSON;
    const json: BasicEventVertexValueJSON = treeJSON[ReferenceTypes.BASIC_EVENTS][key];
    const baseFormula: any = {
      _proxy: "EventReference",
      name: key,
      path: "",
      reference_type: "basic_events",
      tree_id: treeJSON.model_tree_id,
    }
    const m = this.commonCause.length;

    let formulas: any = [];

    const getAllSubsets = (theArray: any) => theArray.reduce(
      (subsets, value) => subsets.concat(
        subsets.map(set => [...set, value])
      ),
      [[]]
    );

    // All common cause subsets, minus the current
    const CCCGSubset = getAllSubsets(this.commonCause.filter((item) => item.key !== key));

    // Populate formulas under new OR Gate
    for (let i=0; i < CCCGSubset.length; i++) {
      const subset = CCCGSubset[i];
      if (subset.length === 0) {
        formulas = [...formulas, {
          ...baseFormula,
          new_name: key + "_I",
          new_label: {
            ...json.label,
            name: json.label.name + "_Independent"
          },
          m,
          k: 1,
          factor: Array.isArray(this.commonCauseFactors) ? this.commonCauseFactors[0] : this.commonCauseFactors
        }]
      } else {
        let new_label = {...json.label };
        let new_label_name: string[] | string = [new_label.name];
        let new_name: string[] | string = [key];
        for (let k=0; k < subset.length; k++) {
          const be = subset[k];
          new_label_name = [...new_label_name, be.value.label.name];
          new_name = [...new_name, be.key];
        }
        new_name = new_name.sort().join("");
        new_label.name = new_label_name.sort().join("_");
        formulas = [...formulas, {
          ...baseFormula,
          new_name,
          new_label,
          m,
          k: subset.length + 1,
          factor: Array.isArray(this.commonCauseFactors) ? this.commonCauseFactors[subset.length] : this.commonCauseFactors
        }]
      }
    }

    const ORJson: GateVertexValueJSON = {
      label: json.label,
      formula: {
        _proxy: ProxyTypes.LOGICAL_EXPRESSION,
        expr: GateTypes.OR,
        formulas
      }
    }
    const gate = this.addLogicalGateFromObject(key, ORJson);
    this.addCCCGEdges(gate, formulas, treeJSON);
    return gate;
  }


  protected addBasicEventFromKey(key: string): BasicEventVertex {
    const treeJSON: FaultTreeMxGraphJSON = this.getJSON() as FaultTreeMxGraphJSON;
    try {
      const json: BasicEventVertexValueJSON = treeJSON[ReferenceTypes.BASIC_EVENTS][key];
      return this.addBasicEventFromObject(key, json);
    } catch (e) {
      console.error('Unable to create basic event from reference', key, e);
      return new BasicEventVertex(key, new BasicEventVertexValue());
    }
  }


  /**
   * @param {string} key
   * @param {HouseEventVertexValueJSON} json
   */
  protected addHouseEventFromObject(key: string, json: HouseEventVertexValueJSON): HouseEventVertex {
    let value = new HouseEventVertexValue(new Label(key), Constant.build(json.constant));
    if (json.label && Object.keys(json.label).length) {
      value = new HouseEventVertexValue(Label.build(json.label), Constant.build(json.constant));
    }
    return new HouseEventVertex(key, value);
  }

  protected addHouseEventFromKey(key: string): HouseEventVertex {
    const treeJSON: FaultTreeMxGraphJSON = this.getJSON() as FaultTreeMxGraphJSON;
    try {
      const json: HouseEventVertexValueJSON = treeJSON[ReferenceTypes.HOUSE_EVENTS][key];
      return this.addHouseEventFromObject(key, json);
    } catch (e) {
      console.error('Unable to create house event from reference', key, e);
      return new HouseEventVertex(key, new HouseEventVertexValue());
    }
  }

  /**
   * @param {string} key
   * @param {FaultTreeMxGraphJSON} json
   */
  protected addTransferGateFromObject(key: string, json: FaultTreeTransferGateVertexValueJSON): FaultTreeTransferGateVertex {
    let Vertex = FaultTreeTransferGateVertex;
    let VertexValue = FaultTreeTransferGateVertexValue;
    if ('make_instance' in json.formula && json.formula.make_instance) {
      Vertex = FaultTreeInstanceTransferGateVertex;
      VertexValue = FaultTreeInstanceTransferGateVertexValue;
    }
    return (new Vertex(key, new VertexValue(Label.build(json.label), new Formula(json.formula))));
  }

  protected addTransferGateFromKey(key: string): FaultTreeTransferGateVertex {
    const treeJSON: FaultTreeMxGraphJSON = this.getJSON() as FaultTreeMxGraphJSON;
    try {
      const json: FaultTreeTransferGateVertexValueJSON = treeJSON[ReferenceTypes.GATES][key];
      return this.addTransferGateFromObject(key, json);
    } catch (e) {
      console.error('Unable to create transfer gate from reference', key, e);
      return new FaultTreeTransferGateVertex(key, new FaultTreeTransferGateVertexValue());
    }
  }
  /**
   * @param {string} key
   * @param {FaultTreeMxGraphJSON} json
   */
  protected addLogicalGateFromObject(key: string, json: GateVertexValueJSON) {
    if (!this.gates[key]) {
      this.gates[key] = this.addVertex(GateVertexBuilder.build(key, json)) as GateVertex;
    }
    return this.gates[key];
  }

  protected addCCCGEdges(gate: GateVertex, formulas: any, json: any): void {
    for (let i=0; i < formulas.length; i++) {
      const formulaJSON: BasicEventVertexValueJSON = JSON.parse(JSON.stringify(json[ReferenceTypes.BASIC_EVENTS][formulas[i].name]));
      const isPointEstimate = formulaJSON.expression._proxy === ProxyTypes.FLOAT;
      const m = formulas[i].m;
      const k = formulas[i].k;
      const getBinomialCoefficient = Utility.factorial(m-1)/Utility.factorial(k-1)*Utility.factorial(m-k);

      // For point estimates
      const Qt = formulaJSON.expression.value;
      const factor = formulas[i].factor.value;

      let getNewValue;
      let userExpression;

      if (!isPointEstimate) {
        // For distribution
        const QtExpression = new Expression(formulaJSON.expression).toString("a", "b");
        const factorExpression = new Expression(factor).toString("c", "d");

        // Generate User expression variables (e.g. a:=1;b:=2;)
        let QtExpressionInit: string | string[] = [QtExpression.substring(QtExpression.indexOf("("), QtExpression.indexOf(",")).slice(1), QtExpression.substring(QtExpression.indexOf(","), QtExpression.indexOf(")")).slice(1)];
        QtExpressionInit = [Utility.insertStr(QtExpressionInit[0], QtExpressionInit[0].indexOf("="), ":") + ";", Utility.insertStr(QtExpressionInit[1], QtExpressionInit[1].indexOf("="), ":") + ";"].join("");
        let factorExpressionInit: string | string[] = [factorExpression.substring(factorExpression.indexOf("("), factorExpression.indexOf(",")).slice(1), factorExpression.substring(factorExpression.indexOf(","), factorExpression.indexOf(")")).slice(1)];
        factorExpressionInit = [Utility.insertStr(factorExpressionInit[0], factorExpressionInit[0].indexOf("="), ":") + ";", Utility.insertStr(factorExpressionInit[1], factorExpressionInit[1].indexOf("="), ":") + ";"].join("");

        // Set User expression
        if (this.commonCause.length === 2) {
          if (i === 0) {
            userExpression = `${QtExpressionInit}${factorExpressionInit}(1-${factorExpression})*${QtExpression}`;
          } else {
            userExpression = `${QtExpressionInit}${factorExpressionInit}${factorExpression} * ${QtExpression}`;
          }
        } else {
          userExpression = `${QtExpressionInit}${factorExpressionInit}${getBinomialCoefficient}*${factorExpression}*${QtExpression}`;
        }
      } else {
        if (this.commonCause.length === 2) {
          if (i === 0) {
            getNewValue = (1-factor)*Qt;
          } else {
            getNewValue = factor * Qt;
          }
        } else {
          getNewValue = (1/getBinomialCoefficient)*factor*Qt;
        }
      }

      formulaJSON.label = formulas[i].new_label;
      if (factor) {
        formulaJSON.expression = {
          _proxy: isPointEstimate ? ProxyTypes.FLOAT : ProxyTypes.STRING_DISTRIBUTION,
          value: isPointEstimate ? getNewValue : undefined,
          user_expression: !isPointEstimate ? userExpression : undefined
        };
      }

      const childVertex = this.addVertex(this.addBasicEventFromObject(formulas[i].new_name, formulaJSON));
      this.addEdgeBetween(gate, childVertex, null);
    }
  }

  /**
   * @param {OutcomeJSON} topNodeReferenceJSON
   * @throws {InvalidJSONFormatError}
   */
  protected addEdgesBetweenVertices(topNodeReferenceJSON: OutcomeJSON): void {
    // empty tree
    if (!topNodeReferenceJSON) return;

    const json = this.getJSON() as FaultTreeMxGraphJSON;
    // @ts-ignore
    const topNodeJSON = json[topNodeReferenceJSON.reference_type][topNodeReferenceJSON.name] as HCLTreeVertexValueJSON;

    if (!topNodeJSON) {
      console.error(`Failed to find vertex value JSON from vertex reference ${JSON.stringify(topNodeReferenceJSON)}`);
      return;
    }

    // base case
    if (topNodeReferenceJSON.reference_type !== ReferenceTypes.GATES) return;

    const key = topNodeReferenceJSON.name;
    // recursive case
    if (topNodeReferenceJSON.reference_type === ReferenceTypes.GATES && !this.visitedLogicGates[key]) {
      const gateVertexValueJSON = topNodeJSON as GateVertexValueJSON;
      const formulaJSON = gateVertexValueJSON.formula as FormulaJSON;
      // add this gate to the visited gates map
      this.visitedLogicGates[key] = true;

      // TODO: backend return null if formulas is empty array
      (formulaJSON.formulas || []).forEach((outcomeJSON) => {
        let childVertex;
        switch (outcomeJSON.reference_type) {
          case ReferenceTypes.BASIC_EVENTS:
            const isCommonCause = this.commonCause && this.commonCause.find((item: any) => item.key === outcomeJSON.name) !== undefined;
            if (isCommonCause) {
              childVertex = this.addCCCGFromObject(outcomeJSON.name);
            } else {
              childVertex = this.addVertex(this.addBasicEventFromKey(outcomeJSON.name));
            }
            break;
          case ReferenceTypes.HOUSE_EVENTS:
            childVertex = this.addVertex(this.addHouseEventFromKey(outcomeJSON.name));
            break;
          case ReferenceTypes.GATES:
            if (this.gates[outcomeJSON.name]) {
              childVertex = this.gates[outcomeJSON.name];
            } else if (outcomeJSON._proxy === ProxyTypes.EVENT_REFERENCE) {
              childVertex = this.addVertex(this.addTransferGateFromKey(outcomeJSON.name));
            }
            break;
          default:
            throw new InvalidJSONFormatError(`Reference type "${outcomeJSON.reference_type}" is not supported.`, this.getJSON());
        }

        // if (!childVertex) throw new InvalidJSONFormatError(`Failed to find vertex from reference json ${JSON.stringify(outcomeJSON)}`, this.getJSON());
        this.addEdgeBetween(this.gates[key], childVertex, null);

        this.addEdgesBetweenVertices(outcomeJSON);
      });
    }
  }

  /**
   * @throws {InvalidJSONFormatError}
   */
  decode(): void {
    super.decode();
    const json = this.getJSON() as FaultTreeMxGraphJSON;

    const addedLogicGates: GateVertex[] = [];
    const transferGateEntries: {[index: string]: GateVertexValueJSON} = {};
    Object.entries(json[ReferenceTypes.GATES]).forEach(([key, value]) => {
      if (value.formula._proxy === ProxyTypes.LOGICAL_EXPRESSION) {
        const gate = this.addLogicalGateFromObject(key, value);
        addedLogicGates.push(gate);
      } else if (value.formula._proxy === ProxyTypes.EVENT_REFERENCE) {
        transferGateEntries[key] = value;
      }
    });

    // this.addEdgesBetweenVertices(json.top_node);
    addedLogicGates.forEach(gate => this.addEdgesBetweenVertices(gate.toReferenceJSON()));

    // handle dangling basic events, house events, transfer gates
    Object.entries(json[ReferenceTypes.BASIC_EVENTS]).forEach(([key, value]) => {
      if (!(this.getGraph().getVerticesWithKey(key).length)) {
        this.addVertex(this.addBasicEventFromObject(key, value));
      }
    });

    Object.entries(json[ReferenceTypes.HOUSE_EVENTS]).forEach(([key, value]) => {
      if (!(this.getGraph().getVerticesWithKey(key).length)) {
        this.addVertex(this.addHouseEventFromObject(key, value));
      }
    });

    Object.entries(transferGateEntries).forEach(([key, value]) => {
      if (!(this.getGraph().getVerticesWithKey(key).length)) {
        this.addVertex(this.addTransferGateFromObject(key, value));
      }
    });
  }
}

export default FaultTreeMxGraphDecoder;
